import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import nanoid from 'nanoid';
import Log from '@/plugin/log/log';
import { Message } from '../message/index';

class HttpRequest {
  private baseUrl: string = '';

  private timeout: number = 10000;

  private instance: AxiosInstance;

  private options: AxiosRequestConfig;

  constructor(baseUrl: string, options: AxiosRequestConfig = {}, timeout: number = 10000) {
    this.baseUrl = baseUrl;
    this.timeout = timeout;
    this.instance = axios.create();
    this.options = Object.assign({}, this.getInsideConfig(), options);
    HttpRequest.interceptors(this.instance);
  }

  getInsideConfig() {
    /* eslint-disable quote-props */
    const auth = JSON.parse(window.sessionStorage.getItem('auth') as string);
    const headers: any = {
      'Content-Type': 'application/json',
      'Accept-Language': 'zh-cn',
    };
    /* eslint-disable no-unused-expressions */
    auth ? headers.Authorization = auth.token : '';
    const config:AxiosRequestConfig = {
      baseURL: this.baseUrl,
      headers,
      timeout: this.timeout,
    };
    return config;
  }

  static interceptors(instance: AxiosInstance) {
    instance.interceptors.request.use((config: any) => {
      Log.info({
        id: config.headers.ID,
        title: `发送${config.method}请求`,
        url: config.url,
      });
      return config;
    });
    instance.interceptors.response.use((res: any) => {
      if (res.status >= 200 && res.status < 300) {
        Log.info({
          id: res.config.headers.ID,
          title: `${res.config.method}请求成功`,
          url: res.config.url,
        });
        if (res.status === 208) return { data: res.data, status: res.status };
        if (res.status !== 200) Message.success(res.data);
        return res.data;
      }
      Log.error({
        id: res.config.headers.ID,
        title: `${res.config.method}请求失败`,
        desc: res.data.msg,
        url: res.config.url,
      });
      throw new Error('error');
    }, (error: any) => {
      if (!navigator.onLine) {
        Message.error('您未连接到互联网, 请检查网络状态');
        throw new Error('network not connect');
      }
      if (error.config.headers && error.config.headers.Reply) {
        throw new Error('not replay');
      }
      if (!error.response) {
        Message.error('服务器发生错误, 请刷新后重试');
        throw new Error('network error');
      }
      Log.error({
        id: error.config.headers.ID,
        title: `${error.config.method}请求失败`,
        desc: error.response.data.msg,
        url: error.response.config.url,
      });
      Message.warning(error.response.data);
      throw new Error('network error');
    });
  }

  get(url: string, options: any = {}) {
    /* eslint-disable no-param-reassign */
    options.headers = Object.assign({ ID: nanoid() }, this.options.headers, options.headers);
    return this.instance.get(url, Object.assign({}, this.options, options));
  }

  post(url: string, data?: any, options: any = {}) {
    /* eslint-disable no-param-reassign */
    options.headers = Object.assign({ ID: nanoid() }, this.options.headers, options.headers);
    return this.instance.post(url, data, Object.assign({}, this.options, options));
  }

  put(url: string, data?: any, options: any = {}) {
    /* eslint-disable no-param-reassign */
    options.headers = Object.assign({ ID: nanoid() }, this.options.headers, options.headers);
    return this.instance.put(url, data, Object.assign({}, this.options, options));
  }

  patch(url: string, data?: any, options: any = {}) {
    /* eslint-disable no-param-reassign */
    options.headers = Object.assign({ ID: nanoid() }, this.options.headers, options.headers);
    return this.instance.patch(url, data, Object.assign({}, this.options, options));
  }

  remove(url: string, options: any = {}) {
    /* eslint-disable no-param-reassign */
    options.headers = Object.assign({ ID: nanoid() }, this.options.headers, options.headers);
    return this.instance.delete(url, Object.assign({}, this.options, options));
  }
}

export default HttpRequest;
